/**
 * Home Page
 * Landing page with features overview and marketing content
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrustIndicator, TrustBadge } from '../components/common/TrustIndicator';
import { JurisdictionSelector } from '../components/common/JurisdictionSelector';
import { ErrorBoundary } from '@/components/ErrorBoundary';

export function Home() {
  // Update page title and meta description for SEO
  useEffect(() => {
    document.title = 'CalWMP - CalGreen Compliance in Minutes | Jurisdiction-Ready Waste Management Plans';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Generate CalGreen-compliant construction waste management plans for California projects. Professional C&amp;D waste diversion documentation designed for San Luis Obispo County contractors and planning departments.'
      );
    }
  }, []);

  return (
    <ErrorBoundary resetKeys={[]}>
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="text-center py-12">
        <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4 leading-tight">
          CalGreen Compliance in Minutes,
          <br />
          <span className="text-primary-600">Not Hours</span>
        </h1>
        <p className="text-xl text-neutral-600 mb-2 max-w-3xl mx-auto">
          Generate jurisdiction-ready waste management plans that meet California&apos;s 65% diversion requirements.
        </p>
        <p className="text-lg text-neutral-500 mb-8">
          CalGreen 5.408.1 Compliant • Designed for SLO County • LEED MRc5 Compatible
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/plans/create" className="btn-primary">
            Create Compliance Plan
          </Link>
          <a href="/docs/calgreen-overview" className="btn-secondary">
            Learn About CalGreen
          </a>
        </div>

        {/* Trust Badge - Show prominently below CTA */}
        <div className="mt-6 flex justify-center">
          <TrustBadge />
        </div>
      </div>

      {/* Trust Indicator - Full version before features */}
      <div className="my-12">
        <TrustIndicator />
      </div>

      {/* SLO County Service Badge - Links to coverage section */}
      <div className="flex justify-center mb-8">
        <a
          href="#coverage"
          className="mt-4 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2 hover:bg-blue-100 transition-colors"
        >
          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium text-blue-800">
            Now serving San Luis Obispo County
          </span>
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </a>
      </div>

      {/* Coverage Area Section */}
      <div id="coverage" className="my-16 scroll-mt-8">
        <h2 className="text-3xl font-bold text-neutral-900 text-center mb-8">
          Serving Your Community
        </h2>
        <JurisdictionSelector />
      </div>

      {/* How It Works - Lifecycle Section */}
      <div className="my-16">
        <h2 className="text-3xl font-bold text-neutral-900 text-center mb-4">
          Complete Compliance Documentation
        </h2>
        <p className="text-center text-neutral-600 mb-10 max-w-2xl mx-auto">
          CalWMP guides you through every phase of waste management compliance—from initial planning to final permit approval.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Step 1 - Plan Generation */}
          <div className="relative">
            <div className="card border-2 border-primary-200 bg-primary-50">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                  Available Now
                </span>
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Generate Compliant Plans</h3>
              <p className="text-neutral-700 mb-4">
                Create jurisdiction-ready construction waste management plans in minutes. Automatic calculations ensure you meet CalGreen 5.408.1 requirements for 65% C&amp;D waste diversion.
              </p>
              <ul className="text-sm text-neutral-600 space-y-2">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  65% diversion calculations
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  SLO County templates
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  LEED MRc5 documentation
                </li>
              </ul>
            </div>
          </div>

          {/* Step 2 - Documentation */}
          <div className="relative">
            <div className="card border-2 border-accent-200 bg-accent-50">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-accent-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  Coming Soon
                </span>
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Track & Document</h3>
              <p className="text-neutral-700 mb-4">
                Monitor actual materials during construction and maintain compliance records. Real-time tracking ensures your diversion goals stay on target.
              </p>
              <ul className="text-sm text-neutral-600 space-y-2">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Real-time material tracking
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Diversion rate monitoring
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Digital weight ticket storage
                </li>
              </ul>
            </div>
          </div>

          {/* Step 3 - Final Reports */}
          <div className="relative">
            <div className="card border-2 border-primary-200 bg-white">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  Coming Soon
                </span>
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Close Out & Report</h3>
              <p className="text-neutral-700 mb-4">
                Generate final compliance reports for planning department approval. Automated summaries prove you met regulatory requirements.
              </p>
              <ul className="text-sm text-neutral-600 space-y-2">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Automated final reports
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Jurisdiction submission ready
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Archive for inspections
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="my-16">
        <h2 className="text-3xl font-bold text-neutral-900 text-center mb-4">
          Professional Compliance Tools for California Contractors
        </h2>
        <p className="text-center text-neutral-600 mb-10 max-w-2xl mx-auto">
          Purpose-built compliance software designed to streamline CalGreen 5.408.1 documentation for California construction projects.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Jurisdiction-Ready Documentation</h3>
            <p className="text-neutral-600">
              Generate construction waste management plans designed to meet CalGreen 5.408.1 requirements and LEED MRc5 (Construction and Demolition Waste Management) documentation standards.
            </p>
          </div>

          <div className="card">
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Automated Calculations</h3>
            <p className="text-neutral-600">
              Automated calculations for diversion rate tracking and material categorization based on CalGreen 5.408.1 requirements.
            </p>
          </div>

          <div className="card">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Audit-Ready Records</h3>
            <p className="text-neutral-600">
              Maintain comprehensive documentation for inspections, final permit approval, and long-term project records.
            </p>
          </div>
        </div>
      </div>

      {/* CalGreen Info Section */}
      <div className="card mt-12 bg-gradient-to-br from-neutral-50 to-neutral-100">
        <h2 className="text-2xl font-bold text-neutral-900 mb-4">
          Understanding CalGreen 5.408.1 Requirements
        </h2>
        <div className="space-y-4 text-neutral-600">
          <p>
            California&apos;s CalGreen building code (Title 24, Part 11, Section 5.408.1) requires construction projects to divert a minimum of <strong className="text-neutral-900">65% of construction and demolition waste</strong> from landfills.
          </p>
          <p>
            Every construction project needs a compliant waste management plan that documents:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><strong>Material types and quantities</strong> - What waste will your project generate?</li>
            <li><strong>Diversion methods</strong> - How will materials be recycled, reused, or salvaged?</li>
            <li><strong>Facility locations</strong> - Where will diverted materials be processed?</li>
            <li><strong>Calculated diversion rates</strong> - Proof you&apos;ll meet the 65% requirement</li>
          </ul>
          <p>
            CalWMP automatically handles these calculations and generates jurisdiction-ready documentation, saving you time and ensuring compliance from day one.
          </p>
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <Link to="/docs/calgreen-overview" className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center">
              Learn more about CalGreen requirements
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Provider Network Section - Below the Fold */}
      <div className="my-16 pt-8 border-t border-neutral-200">
        <h3 className="text-2xl font-semibold text-neutral-900 text-center mb-3">
          Building Our Service Network
        </h3>
        <p className="text-center text-neutral-600 mb-8 max-w-2xl mx-auto">
          We&apos;re partnering with San Luis Obispo County waste haulers, recyclers, and material recovery facilities to help you execute your compliance plans.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-neutral-600">Local service providers</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-sm text-neutral-600">Local to your project</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-sm text-neutral-600">Competitive pricing</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center py-12 mt-8">
        <h2 className="text-3xl font-bold text-neutral-900 mb-4">
          Start Meeting Compliance Requirements Today
        </h2>
        <p className="text-xl text-neutral-600 mb-8 max-w-2xl mx-auto">
          Create your first jurisdiction-ready waste management plan in minutes. No credit card required.
        </p>
        <Link to="/plans/create" className="btn-primary text-lg px-8 py-3">
          Create Plan
        </Link>
      </div>
    </div>
    </ErrorBoundary>
  );
}